import { useState } from "react";
import { loginUserServerFn } from "@/services/auth/login-user-server-fn";
import { googleAuthResponsePayloadSchema } from "@/services/auth/login-user-with-google";
import { loginUserWithGoogleServerFn } from "@/services/auth/login-user-with-google-fn";
import { resendVerification } from "@/services/auth/resend-verification";
import { userProfileQueryOptions } from "@/services/profile/user-profile-query-options";
import { zodResolver } from "@hookform/resolvers/zod";
import { type I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useGoogleLogin } from "@react-oauth/google";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AuthoFormSteps } from "@/constants/auth";
import { Button, ButtonWithLoading } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import Email from "@/assets/icons/e-mail.svg?react";
import DotIcon from "@/assets/icons/ellipse.svg?react";
import Lock from "@/assets/icons/lock.svg?react";
import GoogleLogo from "@/assets/images/google-logo.png";

import AnimatedText from "../animated-text";
import OrSeparator from "../OrSeparator";

// const GOOGLE_REDIRECT_URI = import.meta.env.VITE_GOOGLE_REDIRECT_URI as string;
const GOOGLE_REDIRECT_URI = window.location.origin;

const getLoginFormSchema = (i18n: I18n) =>
  z.object({
    email: z
      .string()
      .min(1)
      .email(i18n._(msg`Invalid email address`)),
    password: z.string().min(1),
  });

const LoginForm = ({
  setAuthFlow,
}: {
  setAuthFlow: (val: { email: string; currentStep: AuthoFormSteps }) => void;
}) => {
  const { t, i18n } = useLingui();

  const { redirect } = useSearch({ from: "/_minimal-layout/_auth-layout/login" });
  const loginUser = useServerFn(loginUserServerFn);
  const loginUserWithGoogle = useServerFn(loginUserWithGoogleServerFn);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const form = useForm({
    resolver: zodResolver(getLoginFormSchema(i18n)),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const router = useRouter();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (
      value:
        | z.infer<ReturnType<typeof getLoginFormSchema>>
        | z.infer<typeof googleAuthResponsePayloadSchema>,
    ) => {
      const response =
        "code" in value
          ? await loginUserWithGoogle({ data: value })
          : await loginUser({ data: value });
      return response;
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: userProfileQueryOptions().queryKey });
      router.invalidate().finally(() => {
        navigate({ to: redirect ?? "/", replace: true });
      });
    },
    onError: async (error, variables) => {
      if (
        "email" in variables &&
        "data" in error &&
        (error.data as { code: number }).code === 409
      ) {
        try {
          await resendVerification(variables.email);
        } catch (e) {
          console.error("Error resending verification:", e);
        } finally {
          setAuthFlow({
            email: variables.email,
            currentStep: AuthoFormSteps.OTP,
          });
        }
      }
    },
  });

  const onSubmit = (
    values:
      | z.infer<ReturnType<typeof getLoginFormSchema>>
      | z.infer<typeof googleAuthResponsePayloadSchema>,
  ) => {
    mutation.mutate(values);
  };

  const login = useGoogleLogin({
    onSuccess: (
      codeResponse: Omit<z.infer<typeof googleAuthResponsePayloadSchema>, "redirect_uri">,
    ) => {
      mutation.mutate({ ...codeResponse, redirect_uri: GOOGLE_REDIRECT_URI });
    },
    flow: "auth-code",
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <p className="mb-2 text-3xl leading-[42px] font-semibold">
          <Trans>Login</Trans>
        </p>
        <p className="mt-2 text-base leading-[26px] font-medium">
          <Trans>Welcome back! Please enter your details</Trans>
        </p>
        <Button
          type="button"
          variant="link"
          size="lg"
          className="mt-6 w-full bg-(--color-bg-color-bg-dark-beige) hover:bg-(--color-bg-color-bg-dark-beige) lg:w-[50%]"
          onClick={login}
          disabled={mutation.isPending}
        >
          <img src={GoogleLogo} alt="Login with Google" width={20} height={20} loading="lazy" />
          <span className="text-base leading-6 font-semibold">
            <Trans>Login with Google</Trans>
          </span>
        </Button>
        <OrSeparator />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="my-5">
              <FormLabel className="text-base leading-[26px] font-medium">
                <Trans>Email Address*</Trans>
              </FormLabel>
              <div className="relative">
                <FormControl>
                  <Input variant="withIcon" placeholder={t`Enter your email`} {...field} />
                </FormControl>
                <Email className="icon size-6" />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="my-5">
              <FormLabel className="text-base leading-[26px] font-medium">
                <Trans>Password*</Trans>
              </FormLabel>
              <div className="relative">
                <FormControl>
                  <Input
                    variant="withIcon"
                    type={showPassword ? "text" : "password"}
                    placeholder="********"
                    {...field}
                  />
                </FormControl>
                <Lock className="icon size-6" />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute end-2 top-1/2 -translate-y-1/2 bg-(--color-bg-color-bg-dark-beige) p-2"
                >
                  {showPassword ? (
                    <EyeOffIcon className="size-5 opacity-90" />
                  ) : (
                    <EyeIcon className="size-5 opacity-90" />
                  )}
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="my-5 flex flex-col items-center justify-between gap-5 md:flex-row">
          <div className="text-sm">
            <Link to="/reset-password" className="text-sm font-medium underline">
              <Trans>Forgot Password?</Trans>
            </Link>
          </div>
        </div>

        <ButtonWithLoading
          type="submit"
          size="lg"
          className="w-full lg:w-[50%]"
          loading={mutation.isPending}
        >
          <DotIcon className="size-2" />
          <AnimatedText>
            <Trans>Login</Trans>
          </AnimatedText>
        </ButtonWithLoading>

        <div className="mt-5">
          <Trans>Do not have an account?</Trans>{" "}
          <Link to="/signup" className="font-semibold underline">
            <Trans>Sign Up</Trans>
          </Link>
        </div>
      </form>
    </Form>
  );
};

export default LoginForm;
